/**
 * @coeldery/ui-kit — **通用後台樣式 tokens**（CSS-in-JS）
 *
 * 規格：
 *   · 只用 CSS 變數（`var(--color-*)`、`var(--shadow-*)`），**唔准 hardcode hex** ✗
 *   · 純 `CSSProperties` 物件＋兩個 factory（`badge(tone)`、`chip(active)`）→ 零 **runtime** 依賴、零邏輯
 *     ⚠️ 唯一 import 係 `import type { CSSProperties } from 'react'` —— **型別用，編譯後消失**；
 *        但型別係公開契約嘅一部分（consumer 拎到嘅 style 物件型別就係佢），
 *        冇 react 型別嘅專案會 typecheck 失敗 → `module.json` 仍然宣告 `react`（type peer，MSR-007）。
 *   · 唔含任何 app 專屬字串／網址／品牌（主題色由使用方喺 CSS 定義 ✓）
 *
 * 來源：`merchant-admin/src/ui.ts`（2026-09-19 由 harvest 流程提取 —— 9 個模組共用同一份，
 *   原本 import `../../src/ui.ts` ＝ 依賴 app 私有檔 → 令模組不可攜 ✗，提取後自我足夠 ✓）。
 *
 * 用法：`import { card, btnPrimary, input, label, errText, hint, badge, chip } from '../ui-kit'`
 */
import type { CSSProperties } from 'react'

const col = (v: string) => `var(${v})`

export const page: CSSProperties = { minHeight: '100svh', backgroundColor: col('--color-bg') }
export const wrap: CSSProperties = { maxWidth: '1080px', margin: '0 auto', padding: '16px 16px 96px' }

export const header: CSSProperties = {
  position: 'sticky', top: 0, zIndex: 30,
  backgroundColor: col('--color-card'), borderBottom: `1px solid ${col('--color-divider')}`,
  paddingTop: 'env(safe-area-inset-top, 0px)',
}
export const headerInner: CSSProperties = {
  maxWidth: '1080px', margin: '0 auto', padding: '10px 16px',
  display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
}
export const h1: CSSProperties = { margin: 0, fontSize: '20px', fontWeight: 800, color: col('--color-text') }

export const card: CSSProperties = {
  backgroundColor: col('--color-card'), borderRadius: '14px',
  boxShadow: col('--shadow-soft'), marginBottom: '10px',
}
export const cardRow: CSSProperties = { ...card, padding: '14px 16px', display: 'flex', gap: '12px', alignItems: 'flex-start', cursor: 'pointer' }

export const btn: CSSProperties = {
  minHeight: '44px', padding: '0 16px', borderRadius: '22px', fontSize: '16px', fontWeight: 700,
  border: `2px solid ${col('--color-primary')}`, backgroundColor: col('--color-card'),
  color: col('--color-primary'), cursor: 'pointer',
}
export const btnPrimary: CSSProperties = {
  ...btn, border: 'none', backgroundColor: col('--color-primary'), color: '#fff',
}
export const btnDanger: CSSProperties = { ...btn, borderColor: col('--color-accent'), color: col('--color-accent') }
export const btnGhost: CSSProperties = { ...btn, border: `1px solid ${col('--color-divider')}`, color: col('--color-text-secondary') }

export const input: CSSProperties = {
  width: '100%', minHeight: '44px', padding: '8px 12px', boxSizing: 'border-box',
  fontSize: '16px', borderRadius: '10px', border: `1.5px solid ${col('--color-divider')}`,
  backgroundColor: col('--color-card'), color: col('--color-text'),
}
export const inputError: CSSProperties = { ...input, borderColor: col('--color-accent') }
export const label: CSSProperties = { fontSize: '14px', fontWeight: 700, color: col('--color-text'), marginBottom: '4px', display: 'block' }
export const hint: CSSProperties = { fontSize: '13px', color: col('--color-text-secondary'), margin: '4px 0 0' }
export const errText: CSSProperties = { fontSize: '13px', color: col('--color-accent'), margin: '4px 0 0' }

export const badge = (tone: 'ok' | 'off' | 'warn' | 'sponsored' | 'info'): CSSProperties => {
  const map = {
    ok: { bg: col('--color-primary'), fg: '#fff' },
    off: { bg: col('--color-off-bg'), fg: col('--color-off-text') },
    warn: { bg: col('--color-warn-bg'), fg: col('--color-warn-text') },
    sponsored: { bg: col('--color-sponsored'), fg: '#fff' },
    info: { bg: col('--color-divider'), fg: col('--color-text') },
  }[tone]
  return {
    display: 'inline-block', fontSize: '12px', fontWeight: 700, padding: '2px 8px',
    borderRadius: '999px', backgroundColor: map.bg, color: map.fg, whiteSpace: 'nowrap',
  }
}

export const modalScrim: CSSProperties = {
  position: 'fixed', inset: 0, backgroundColor: col('--overlay-scrim'), zIndex: 100,
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto',
  padding: '16px', boxSizing: 'border-box',
}
export const modal: CSSProperties = {
  width: '100%', maxWidth: '720px', backgroundColor: col('--color-card'), borderRadius: '16px',
  padding: '18px', boxSizing: 'border-box',
}
export const sectionTitle: CSSProperties = {
  margin: '20px 0 8px', fontSize: '15px', fontWeight: 800, color: col('--color-text'),
  borderTop: `1px solid ${col('--color-divider')}`, paddingTop: '14px',
}
export const grid2: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }
export const chip = (active: boolean): CSSProperties => ({
  minHeight: '34px', padding: '4px 12px', borderRadius: '999px', fontSize: '14px', fontWeight: 600,
  border: `1.5px solid ${active ? col('--color-primary') : col('--color-divider')}`,
  backgroundColor: active ? col('--color-primary') : col('--color-card'),
  color: active ? '#fff' : col('--color-text'), cursor: 'pointer',
})
